import m from "mithril";
import Requester from "../../shared/request";
import Toasts from "../../shared/toasts";
import Gateway from "../../shared/app_settings";
import i18n from "../../shared/i18n/i18n";
import RouterService from "../../shared/services/router.service";

let loading = true, filter, search, courses, orderBy;
let categoryFilter = [];
let categoryDropdownOpen = false;
let categoryDropdownSearch = "";
let categoryTooltip = { courseId: null };
let categoryTooltipTimer = null;

export function loadCourses(unitId) {
    loading = true;
    courses = null;
    Requester.get(Gateway.admin + "units/" + unitId + "/courses").then(function (result) {
        courses = result.entities;
    }).catch(function (error) {
        Toasts.add("error", i18n.t("error"), i18n.t(error.response.properties.errorCode));
    }).finally(function () {
        loading = false;
        m.redraw();
    });
}

export function getCourseCount() {
    return courses ? courses.length : 0;
}

function getCategories() {
    if (!courses) return [];
    const map = {};
    courses.forEach(function (c) {
        (c.properties.categories || []).forEach(function (cat) {
            if (cat.name && !map[cat.name]) map[cat.name] = cat;
        });
    });
    return Object.values(map);
}

function getFilteredCourses() {
    if (!courses) return [];
    let result = courses.slice();

    if (filter === "active") result = result.filter(function (c) { return c.properties.isActive; });
    if (filter === "inactive") result = result.filter(function (c) { return !c.properties.isActive; });

    if (categoryFilter.length > 0) {
        result = result.filter(function (c) {
            const cats = c.properties.categories || [];
            return categoryFilter.some(function (f) {
                return cats.some(function (cat) { return cat.name && cat.name.toLowerCase() === f; });
            });
        });
    }

    if (search) {
        const q = search.toLowerCase();
        result = result.filter(function (c) { return c.properties.name && c.properties.name.toLowerCase().indexOf(q) !== -1; });
    }

    if (orderBy === "courseName") result.sort(function (a, b) { return a.properties.name.localeCompare(b.properties.name); });
    if (orderBy === "courseNameDesc") result.sort(function (a, b) { return b.properties.name.localeCompare(a.properties.name); });

    return result;
}

function setOrderByCourseName() {
    if (orderBy === "courseName") {
        orderBy = "courseNameDesc";
    } else if (orderBy === "courseNameDesc") {
        orderBy = null;
    } else {
        orderBy = "courseName";
    }
}
function renderCategoryDropdownLabel(categories) {
    if (categoryFilter && categoryFilter.length > 0) {
        if (categoryFilter.length === 1) {
            const matched = categories.find(function (c) {
                return c.name.toLowerCase() === categoryFilter[0];
            });
            if (matched) {
                return matched.isEditable
                    ? m("span", { style: "overflow: hidden; white-space: nowrap; text-overflow: ellipsis; max-width: 150px; display: inline-block; vertical-align: middle;" }, matched.name)
                    : i18n.t("category_" + matched.name.toLowerCase().replace(/\s+/g, "_"));
            }
        }
        return categoryFilter.length + " " + i18n.t("selected");
    }
    return i18n.t("all_types");
}

function renderCategoryDropdownItems() {
    const categories = getCategories();
    const q = categoryDropdownSearch.toLowerCase();
    const standard = categories
        .filter(function (c) { return !c.isEditable; })
        .sort(function (a, b) { return a.name.localeCompare(b.name); });
    const custom = categories
        .filter(function (c) { return c.isEditable; })
        .sort(function (a, b) { return a.name.toLowerCase().localeCompare(b.name.toLowerCase()); });

    function matchesSearch(item) {
        if (!q) return true;
        const label = item.isEditable
            ? item.name
            : i18n.t("category_" + item.name.toLowerCase().replace(/\s+/g, "_"));
        return label.toLowerCase().indexOf(q) !== -1;
    }

    function renderItem(item) {
        const key = item.name.toLowerCase();
        const isSelected = categoryFilter.includes(key);
        const label = item.isEditable
            ? item.name
            : i18n.t("category_" + item.name.toLowerCase().replace(/\s+/g, "_"));
        return m("div", {
            class: isSelected ? "active flex align-center" : "flex align-center",
            onclick: function () {
                if (isSelected) {
                    categoryFilter = categoryFilter.filter(function (c) { return c !== key; });
                } else {
                    categoryFilter.push(key);
                }
                filter = null;
            }
        }, [
            m("i", { class: isSelected ? "icon-checkbox-checked mr-2" : "icon-checkbox-empty mr-2" }),
            item.isEditable
                ? m("span", { style: "overflow: hidden; white-space: nowrap; text-overflow: ellipsis; min-width: 0; max-width: 160px; display: inline-block;" }, label)
                : label
        ]);
    }

    const filteredStandard = standard.filter(matchesSearch);
    const filteredCustom = custom.filter(matchesSearch);
    const items = filteredStandard.map(renderItem);

    if (filteredCustom.length > 0) {
        items.push(m(".dropdown-section-label", {
            style: "padding: 0.4rem 0.8rem; font-size: 0.75rem; opacity: 0.6; cursor: default",
            onclick: function (e) { e.stopPropagation(); }
        }, i18n.t("custom_course_categories")));
        filteredCustom.forEach(function (c) { items.push(renderItem(c)); });
    }

    if (items.length === 0) {
        return [m("div", { style: "padding: 0.5rem 0.8rem; font-size: 0.875rem; opacity: 0.6;" }, i18n.t("no_results"))];
    }
    return items;
}

function renderCategoryDropdown() {
    const categories = getCategories();
    return m(".dropdown", { class: categoryDropdownOpen ? "open" : "" },
        m(".button.with-icon pl-2 .btn.btn-lg", {
            onclick: function (e) {
                e.stopPropagation();
                categoryDropdownOpen = !categoryDropdownOpen;
                if (categoryDropdownOpen) categoryDropdownSearch = "";
            }
        }, [
            renderCategoryDropdownLabel(categories),
            m("i.icon-back rotate-270 ml-2")
        ]),
        categoryDropdownOpen
        ? m(".dropdown-list", {
            style: "min-width: 220px;",
            onclick: function (e) { e.stopPropagation(); },
            oncreate: function () {
                document.addEventListener("click", function closeOnOutside() {
                    categoryDropdownOpen = false;
                    m.redraw();
                    document.removeEventListener("click", closeOnOutside);
                });
            }
        }, [
            // Search field inside dropdown
            m("div", {
                style: "padding: 0.4rem 0.5rem; border-bottom: 1px solid var(--border-color, #e5e7eb);",
                onclick: function (e) { e.stopPropagation(); }
            }, [
                m("input[type=text]", {
                    placeholder: i18n.t("search"),
                    value: categoryDropdownSearch,
                    oninput: function () { categoryDropdownSearch = this.value; },
                    onkeydown: function (e) {
                        if (e.key === "Escape") { categoryDropdownSearch = ""; }
                        e.stopPropagation();
                    },
                    style: "width: 100%; padding: 0.25rem 0.4rem; font-size: 0.875rem; border: 1px solid var(--border-color, #d1d5db); border-radius: 4px;"
                })
            ]),
            renderCategoryDropdownItems()
        ])
        : null
    );
}

function renderStatusDropdown() {
    const label = filter === "active" ? i18n.t("active") : filter === "inactive" ? i18n.t("inactive") : i18n.t("all_courses");
    return m(".dropdown",
        m(".button.with-icon pl-2 .btn.btn-lg", [
            label,
            m("i.icon-back rotate-270 ml-2")
        ]),
        m(".dropdown-list", [
            m("div", {
                class: !filter && categoryFilter.length === 0 ? "active" : "",
                onclick: function () { filter = null; categoryFilter = []; }
            }, i18n.t("all_courses")),
            m("div", {
                class: filter === "active" ? "active" : "",
                onclick: function () { filter = "active"; categoryFilter = []; }
            }, i18n.t("active")),
            m("div", {
                class: filter === "inactive" ? "active" : "",
                onclick: function () { filter = "inactive"; categoryFilter = []; }
            }, i18n.t("inactive"))
        ])
    );
}

function renderSearchInput() {
    return m(".has-icon-right", [
        m("input[type=search]", {
            oninput: function () { search = this.value; },
            value: search,
            placeholder: i18n.t("search")
        }),
        m("i.icon-x c-hand", { onclick: function () { search = null; } }),
        m("i.icon-search c-hand")
    ]);
}

function renderCategoryTags(course) {
    const cats = course.properties.categories && course.properties.categories.length > 0
        ? course.properties.categories
        : null;
    if (!cats) {
        return m("span.category-tag", i18n.t("category_unassigned"));
    }
    // Sort: standard (non-editable) alpha first, then custom (editable) alpha
    const sorted = cats.slice().sort(function (a, b) {
        if (!!a.isEditable !== !!b.isEditable) return a.isEditable ? 1 : -1;
        return (a.name || "").toLowerCase().localeCompare((b.name || "").toLowerCase());
    });
    function renderTag(cat) {
        if (cat.isEditable) {
            return m("span.category-tag", { class: "category-custom", key: cat.id || cat.name }, cat.name);
        }
        return m("span.category-tag", {
            class: cat.name ? cat.name.replace(/\s+/g, "-").toLocaleLowerCase() : "",
            key: cat.name
        }, i18n.t("category_" + (cat.name || "unassigned").replace(/\s+/g, "_").toLocaleLowerCase()));
    }
    if (sorted.length <= 3) {
        return sorted.map(renderTag);
    }
    // More than 3: show first 3 + info icon tooltip
    const visible = sorted.slice(0, 3);
    const overflow = sorted.slice(3);
    const courseId = course.properties.id;
    return [
        visible.map(renderTag),
        m("span", { style: "position: relative; display: inline-block; vertical-align: middle; margin-left: 4px;" }, [
            m("span", {
                style: "display: inline-flex; align-items: center; justify-content: center; width: 16px; height: 16px; border-radius: 50%; border: 1.5px solid var(--color-primary, #3b82f6); font-size: 0.65rem; font-weight: 700; color: var(--color-primary, #3b82f6); cursor: help; font-style: italic; line-height: 1;",
                onmouseenter: function () {
                    if (categoryTooltipTimer) clearTimeout(categoryTooltipTimer);
                    categoryTooltip = { courseId: courseId };
                    m.redraw();
                },
                onmouseleave: function () {
                    categoryTooltipTimer = setTimeout(function () {
                        categoryTooltip = { courseId: null };
                        m.redraw();
                    }, 100);
                }
            }, "i"),
            categoryTooltip.courseId === courseId
                ? m("div", {
                    style: "position: absolute; left: 1.4rem; top: -0.3rem; z-index: 200; background: var(--lighter); border: 1px solid var(--border); border-radius: 6px; padding: 0.5rem 0.75rem; min-width: 140px; max-width: 260px; box-shadow: 0 4px 12px rgba(0,0,0,0.12); font-size: 0.82rem; white-space: nowrap; color: var(--dark);",
                    onmouseenter: function () {
                        if (categoryTooltipTimer) clearTimeout(categoryTooltipTimer);
                    },
                    onmouseleave: function () {
                        categoryTooltip = { courseId: null };
                        m.redraw();
                    }
                }, overflow.map(function (cat) {
                    const text = cat.isEditable
                        ? cat.name
                        : i18n.t("category_" + (cat.name || "unassigned").replace(/\s+/g, "_").toLocaleLowerCase());
                    const style = cat.isEditable
                        ? "padding: 0.12rem 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 210px;"
                        : "padding: 0.12rem 0;";
                    return m("div", { style: style }, text);
                }))
                : null
        ])
    ];
}

function renderCourseRow(course) {
    const href = RouterService.getCourseUrl(course.properties.id);
    return m("tr", [
        m("td.text-bolder", m(m.route.Link, { href: href }, course.properties.name)),
        m("td", renderCategoryTags(course)),
        m("td", m(m.route.Link, { href: href }, course.properties.version)),
        m("td", m(m.route.Link, { href: href, tabindex: "-1" }, course.properties.isActive ? m("i.icon-check") : "")),
    ]);
}

function renderCourseTable() {
    const fc = getFilteredCourses();
    if (fc.length === 0) return m(".text-gray padding", i18n.t("no_courses"));
    const sortIconClass = orderBy === "courseName" ? "rotate-90" : orderBy === "courseNameDesc" ? "rotate-270" : "rotate-90 translucent";
    return m("table.full-width", [
        m("thead",
            m("tr", [
                m("th.c-hand", { onclick: setOrderByCourseName }, [i18n.t("name"), m("i.icon-forward align-middle", { class: sortIconClass })]),
                m("th", i18n.t("course_type")),
                m("th", i18n.t("latest_version")),
                m("th", i18n.t("active")),
            ])
        ),
        m("tbody", fc.map(renderCourseRow))
    ]);
}

function renderBody() {
    if (loading) return m(".text-gray padding", [i18n.t("loading"), m(".loading-dots")]);
    if (!courses) return m(".text-gray padding", i18n.t("unable_to_load_courses"));
    return m(".table-wrapper flex-auto", renderCourseTable());
}

const Unit_Courses = {
    oninit: function (vnode) {
        loadCourses(vnode.attrs.unitId);
    },
    view: function () {
        return [
            m(".content-box", [
                m(".toolbar", { class: loading ? "disable-buttons" : "" }, [
                    m(".toolbar-section", [
                        renderCategoryDropdown(),
                        renderStatusDropdown(),
                        renderSearchInput(),
                    ])
                ]),
                m(".flex-column", renderBody())
            ])
        ];
    }
};

export default Unit_Courses;
